import React, { Component } from 'react';
import Link from '@atlaskit/link';
import { Text } from '@atlaskit/primitives/compiled';
import { fg } from '@atlassian/jira-feature-gating';
import type { Board, PublicBoard } from '@atlassian/jira-boards-common/src/model/index.tsx';
import { FormattedMessage } from '@atlassian/jira-intl';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { SprintInformation, Sprint } from '../../model/index.tsx';
import messages from './messages.tsx';

type Props = {
	board: Board;
	sprintInformation: SprintInformation;
	intl: IntlShape;
};

// eslint-disable-next-line jira/react/no-class-components
class Content extends Component<Props> {
	renderContentForPrivateBoard() {
		const {
			intl: { formatMessage },
		} = this.props;

		return (
			// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
			<p>
				{formatMessage(
					fg('jira-issue-terminology-update-m4')
						? messages.privateBoardMessageIssueTerminologyRefreshM4
						: messages.privateBoardMessage,
				)}
			</p>
		);
	}

	renderLearnMoreLink(board: PublicBoard) {
		const {
			intl: { formatMessage },
		} = this.props;

		return (
			<>
				<Text as="p">
					{formatMessage(messages.activeSprintsRecommendation, { boardName: board.name })}
				</Text>
				<Text as="p">
					<Text as="em">
						{fg('replace-anchor-with-link__jira-cosmos') ? (
							<Link href="https://confluence.atlassian.com/jirasoftwarecloud/running-sprints-in-a-scrum-project-764478111.html">
								{formatMessage(messages.link)}
							</Link>
						) : (
							// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
							<a href="https://confluence.atlassian.com/jirasoftwarecloud/running-sprints-in-a-scrum-project-764478111.html">
								{formatMessage(messages.link)}
							</a>
						)}
					</Text>
				</Text>
			</>
		);
	}

	renderContentForNoSprints(board: PublicBoard) {
		const {
			intl: { formatMessage },
		} = this.props;

		return (
			// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
			<p>
				{formatMessage(
					fg('jira-issue-terminology-update-m4')
						? messages.noSprintsMessageIssueTerminologyRefreshM4
						: messages.noSprintsMessage,
					{ boardName: board.name },
				)}
			</p>
		);
	}

	renderContentForSingleSprint(board: PublicBoard, sprint: Sprint) {
		return (
			<>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p>
					<FormattedMessage
						{...(fg('jira-issue-terminology-update-m4')
							? messages.singleSprintMessageIssueTerminologyRefreshM4
							: messages.singleSprintMessage)}
						// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
						values={{ sprintName: <a href={sprint.url}>{sprint.name}</a> }}
					/>
				</p>
				{this.renderLearnMoreLink(board)}
			</>
		);
	}

	renderContentForMultipleSprints(board: PublicBoard, sprints: Sprint[]) {
		const {
			intl: { formatMessage },
		} = this.props;
		return (
			<>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p>
					{formatMessage(
						fg('jira-issue-terminology-update-m4')
							? messages.multipleSprintsMessageIssueTerminologyRefreshM4
							: messages.multipleSprintsMessage,
					)}
				</p>
				<ul>
					{sprints.map(({ name, url }, index) => (
						<li key={index}>
							{fg('replace-anchor-with-link__jira-cosmos') ? (
								<Link href={url}>{name}</Link>
							) : (
								// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
								<a href={url}>{name}</a>
							)}
						</li>
					))}
				</ul>
				{this.renderLearnMoreLink(board)}
			</>
		);
	}

	renderContentForSprintRetrievalError(board: PublicBoard) {
		const {
			intl: { formatMessage },
		} = this.props;

		return (
			<>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p>
					{formatMessage(
						fg('jira-issue-terminology-update-m4')
							? messages.retrievalFailedMessageIssueTerminologyRefreshM4
							: messages.retrievalFailedMessage,
						{ boardName: board.name },
					)}
				</p>
				{this.renderLearnMoreLink(board)}
			</>
		);
	}

	render() {
		const { board, sprintInformation } = this.props;
		// @ts-expect-error - TS2339 - Property 'isPrivate' does not exist on type 'Board'.
		if (board.isPrivate) {
			return this.renderContentForPrivateBoard();
		}
		// @ts-expect-error - TS2339 - Property 'error' does not exist on type 'SprintInformation'.
		if (sprintInformation.error) {
			// @ts-expect-error - TS2345 - Argument of type 'Board' is not assignable to parameter of type 'PublicBoard'.
			return this.renderContentForSprintRetrievalError(board);
		}
		// @ts-expect-error - TS2339 - Property 'sprints' does not exist on type 'SprintInformation'.
		if (sprintInformation.sprints.length === 1) {
			// @ts-expect-error - TS2345 - Argument of type 'Board' is not assignable to parameter of type 'PublicBoard'. | TS2339 - Property 'sprints' does not exist on type 'SprintInformation'.
			return this.renderContentForSingleSprint(board, sprintInformation.sprints[0]);
		}
		// @ts-expect-error - TS2339 - Property 'sprints' does not exist on type 'SprintInformation'.
		if (sprintInformation.sprints.length > 1) {
			// @ts-expect-error - TS2345 - Argument of type 'Board' is not assignable to parameter of type 'PublicBoard'. | TS2339 - Property 'sprints' does not exist on type 'SprintInformation'.
			return this.renderContentForMultipleSprints(board, sprintInformation.sprints);
		}
		// @ts-expect-error - TS2345 - Argument of type 'Board' is not assignable to parameter of type 'PublicBoard'.
		return this.renderContentForNoSprints(board);
	}
}

export default injectIntl(Content);
